import '../styles.css';
import Button from '../components/Button';
import PageHeader from '../components/PageHeader';
import { ArrowIcon } from '../icons/navigationIcons';
import { storageAssets } from '../storage_assets';
import IterationStatus from '../components/iterationStatus';
import { openExternal } from '../utils/openExternal';

/* Typography: see styles.css — .text-header-main, .text-body, .text-body-sm, .text-inline-code */

const ProjectSevenPage = () => {
    return (
        <div className="scrollable-window scrollable-window--wide">
            <div style={styles.wholeThingBox}>
                <div style={styles.boxMain}>
                    <PageHeader
                        title="Design review command"
                        action={
                            <Button
                                variant="text"
                                text="View repo"
                                icon={<ArrowIcon />}
                                iconPosition="end"
                                onClick={() => openExternal('https://github.com/aoshumway8/knowledge-base')}
                            />
                        }
                    />
                    <div className="text-body-sm">
                        Scaling design quality as a solo designer in an AI-accelerated engineering org.
                    </div>
                    <div style={styles.deviderStroke}></div>

                    <div className="text-body">The problem</div>
                    <div className="text-body-sm">
                        After our Director of Design was laid off, I became the sole designer supporting a team of five engineers at AskElephant.
                        Each engineer was heavily augmented with AI tooling, so they shipped code as if they were fifteen engineers.
                        Features moved faster than I could reasonably review, critique, or fully design. I needed a way to scale
                        design oversight without becoming a bottleneck.
                    </div>

                    <div className="text-body">First attempt: experts only</div>
                    <div className="text-body-sm">
                        To solve this issue, I created the <code className="text-inline-code">/design-review</code> command in .cursor/skills. It was a panel of AI
                        personas modeled after real design experts — Susan Weinschenk, Don Norman...and many others. Each
                        brought deep knowledge of psychology, heuristics, and product thinking.
                    </div>
                    <div className="text-body-sm">
                        It did not work well. The experts operated in silos. Each one zeroed in on their own domain and missed the
                        bigger picture. Weinschenk flagged cognitive load. Norman cited affordances. Fitzpatrick questioned value
                        clarity. The feedback was technically correct but collectively incoherent — nobody was talking about the same
                        problem from the same starting point.
                    </div>

                    <div className="text-body">The insight</div>
                    <div className="text-body-sm">
                        In real design practice, expert knowledge only becomes useful when it is grounded in observed user behavior.
                        Experts do not diagnose in a vacuum — they respond to what real people actually do. A heuristic violation
                        only matters if someone actually stumbles on it.
                    </div>
                    <div className="text-body-sm">
                        I needed a layer of raw, unfiltered user reaction to run first. Something that would give the experts a
                        shared problem to respond to, rather than letting each one go hunting for their favorite category of issue.
                    </div>

                    <div className="text-body">A tester panel gathers raw reactions</div>
                    <div className="text-body-sm">
                        I built a set of purposefully extreme, exaggerated user personas. Extreme by design — the goal was to force
                        thorough, honest reactions rather than polite hedging. Testers do not diagnose or suggest fixes. They just
                        react. Things like: "this button feels off." That is it. A few examples from the full panel:
                    </div>
                    <div style={styles.personaList}>
                        {[
                            {
                                initials: 'D',
                                name: 'Darryl',
                                imgKey: 'testerDarryl',
                                desc: 'A 24-year-old Austin warehouse worker and freelance video editor with ADHD and dyslexia who built his own PC from YouTube. Will bail in about fifteen seconds if the main action is not obvious. Never files a bug.',
                            },
                            {
                                initials: 'Do',
                                name: 'Dorothy',
                                imgKey: 'testerDorothy',
                                desc: 'A 72-year-old retired Boise schoolteacher who has never owned a smartphone, still uses a rotary landline, and calls every computer "the machine." Half the time has to look up which button means "send."',
                            },
                            {
                                initials: 'L',
                                name: 'Luna',
                                imgKey: 'testerLuna',
                                desc: 'A San Diego surfer and photographer raised on Danish quiet luxury. Has never opened Figma and doesn\'t know what a type scale is. Can tell good vibes from bad in five seconds: "clean," "choppy," "stoked."',
                            },
                        ].map(({ initials, name, imgKey, desc }) => (
                            <div key={name} style={styles.personaCard}>
                                <div style={styles.personaAvatar}>
                                    {storageAssets[imgKey] ? (
                                        <img src={storageAssets[imgKey]} alt={name} style={styles.personaAvatarImg} />
                                    ) : (
                                        <span style={styles.personaInitials}>{initials}</span>
                                    )}
                                </div>
                                <div style={styles.personaText}>
                                    <div className="text-body-sm" style={styles.personaName}>{name}</div>
                                    <div className="text-body-sm">{desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-body">Expert agents make sense of the data</div>
                    <div className="text-body-sm">
                        After the testers run, the expert panel receives their raw reactions as input. It is the experts' job to
                        determine why a tester felt something was off — and what to do about it. Each expert is grounded in real
                        books and primary research, not generic UX advice. A few examples from the full panel:
                    </div>
                    <div style={styles.personaList}>
                        {[
                            {
                                initials: 'SW',
                                name: 'Susan M. Weinschenk, Ph.D.',
                                imgKey: 'expertSusan',
                                desc: 'Psychologist and author of 100 Things Every Designer Needs to Know About People. Applied behavioral science for design.',
                            },
                            {
                                initials: 'RW',
                                name: 'Richard West, Ph.D.',
                                imgKey: 'expertRich',
                                desc: "Psychology professor whose life's work covers learning, cognitive bias, and decision-making.",
                            },
                            {
                                initials: 'DN',
                                name: 'Don Norman',
                                imgKey: 'expertDon',
                                desc: 'The father of user experience. Brings heuristic analysis and systems thinking rooted in decades of research.',
                            },
                        ].map(({ initials, name, imgKey, desc }) => (
                            <div key={name} style={styles.personaCard}>
                                <div style={styles.personaAvatar}>
                                    {storageAssets[imgKey] ? (
                                        <img src={storageAssets[imgKey]} alt={name} style={styles.personaAvatarImg} />
                                    ) : (
                                        <span style={styles.personaInitials}>{initials}</span>
                                    )}
                                </div>
                                <div style={styles.personaText}>
                                    <div className="text-body-sm" style={styles.personaName}>{name}</div>
                                    <div className="text-body-sm">{desc}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-body">How it works</div>
                    <div className="text-body-sm">
                        Designers and engineers type <code className="text-inline-code">/design-review</code>. The tester panel
                        runs first, producing direct, unfiltered reactions. Then the expert panel reads those reactions and
                        synthesizes structured feedback: friction points, heuristic violations, clarity risks, and concrete
                        improvement suggestions. The experts are anchored to a real problem — not just their own frameworks.
                    </div>

                    <div className="text-body">2 AI generated prototpes - same prompt</div>

                    <div style={styles.compareGrid}>
                        <div style={styles.compareColumn}>
                            <div className="text-body-sm" style={styles.compareLabel}>
                                Without <code className="text-inline-code">/design-review</code>
                            </div>
                            <img
                                src={storageAssets.aiNotDesignedUI}
                                alt="AI-generated sign-in screen without design review"
                                style={styles.compareImage}
                            />
                            <IterationStatus
                                items={[
                                    {
                                        type: 'issue',
                                        text: '“Sign in with your account” reads like an info card, not a button.',
                                    },
                                    {
                                        type: 'issue',
                                        text: '“Your settings and calendar connections will carry over from the web app” only helps heavy web users; everyone else is left guessing.',
                                    },
                                    {
                                        type: 'issue',
                                        text: 'No explanation of why sign-in is required first — nothing connects this step to what the user gets next.',
                                    },
                                    {
                                        type: 'issue',
                                        text: '“Already signed in on the web? Your meetings and notes are ready to sync” is passive, with no action or CTA — a dead end.',
                                    },
                                ]}
                            />
                        </div>
                        <div style={styles.compareColumn}>
                            <div className="text-body-sm" style={styles.compareLabel}>
                                With <code className="text-inline-code">/design-review</code>
                            </div>
                            <img
                                src={storageAssets.aiDesignedUI}
                                alt="AI-generated sign-in screen with design review"
                                style={styles.compareImage}
                            />
                            <IterationStatus
                                items={[
                                    {
                                        type: 'success',
                                        text: 'Both paths are styled as equal, clearly interactive buttons — email and password are no longer a mystery tap target.',
                                    },
                                    {
                                        type: 'success',
                                        text: 'Equal visual weight on both options with an “— or —” divider — neither path looks like the default.',
                                    },
                                    {
                                        type: 'success',
                                        text: '“Sign in to get started” is clear and neutral; it works for every user regardless of web history.',
                                    },
                                    {
                                        type: 'success',
                                        text: 'A one-line value hook explains what sign-in unlocks and what comes next — users get a reason before being asked to comply.',
                                    },
                                ]}
                            />
                        </div>
                    </div>

                    <div className="text-body">Impact</div>
                    <div className="text-body-sm">
                        <ul style={styles.bulletList}>
                            <li>Scaled design coverage without adding headcount.</li>
                            <li>Caught usability issues before shipping.</li>
                            <li>Let engineers self-serve design feedback and ship with confidence.</li>
                            <li>Preserved human oversight for high-stakes decisions.</li>
                        </ul>
                    </div>

                    <div className="text-body">Key takeaway</div>
                    <div className="text-body-sm">
                        In high-velocity, AI-accelerated teams, the bottleneck shifts from execution to decision-making. Combining
                        expert heuristics, realistic user reactions, and instant accessibility scales design thinking, not just design
                        output.
                    </div>

                    <div className="text-body">Reflection</div>
                    <div className="text-body-sm">
                        This project changed how I think about design. Design is not only crafting interfaces. It is building systems
                        that help good decisions happen consistently, even when you are not in the room.
                    </div>
                </div>
            </div>
        </div>
    );
};

const styles = {
    wholeThingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '40px',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
    },
    boxMain: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '20px',
        width: 'var(--content-width)',
        maxWidth: '100%',
        minWidth: 0,
        padding: '0 clamp(1rem, 4vw, 2rem)',
        boxSizing: 'border-box',
        margin: '0',
        position: 'relative',
        zIndex: 1,
    },
    image: {
        width: 'var(--content-media-width)',
        height: 'auto',
        maxWidth: '100%',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
    },
    deviderStroke: {
        height: '1px',
        backgroundColor: 'var(--color-border)',
        alignSelf: 'stretch',
    },
    bulletList: {
        marginTop: '10px',
        marginBottom: '0px',
        paddingLeft: '20px',
    },
    numberList: {
        marginTop: '10px',
        marginBottom: '0px',
        paddingLeft: '20px',
        display: 'grid',
        gap: '12px',
    },
    compareGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'clamp(24px, 4vw, 40px)',
        alignSelf: 'stretch',
        width: '100%',
        minWidth: 0,
    },
    compareColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: '16px',
        minWidth: 0,
    },
    compareLabel: {
        fontWeight: 600,
    },
    compareImage: {
        width: '100%',
        height: 'auto',
        maxWidth: '100%',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
    },
    personaList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        alignSelf: 'stretch',
    },
    personaCard: {
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
    },
    personaAvatar: {
        width: '80px',
        height: '80px',
        borderRadius: 'var(--radius-md)',
        flexShrink: 0,
        backgroundColor: 'var(--color-border)',
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    personaAvatarImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    personaInitials: {
        fontSize: '14px',
        fontWeight: 600,
        color: 'var(--color-text-secondary)',
        userSelect: 'none',
    },
    personaText: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        paddingTop: '2px',
    },
    personaName: {
        fontWeight: 600,
    },
};

export default ProjectSevenPage;
